import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { normalizeAvatar, seededAvatar, type AvatarConfig, type Facing } from './avatars';
import { sanitizePosition } from './map';

/**
 * Digital Office — live multiplayer layer.
 *
 * Everything "who is here right now, and where" rides on one Supabase
 * Realtime channel per organisation:
 *
 *   • presence  — identity + status + avatar look + a coarse position.
 *                 Tracked on join, re-tracked when something slow changes
 *                 (status flips, avatar edited, character stops) and on a
 *                 slow heartbeat so late joiners always get a fresh spot.
 *                 Presence is what tells us who is ONLINE; a profile that
 *                 isn't in the presence state is offline.
 *   • broadcast — `move` events at ~11 Hz while a character is walking,
 *                 and `emote` events. Broadcast is fire-and-forget and much
 *                 cheaper than diffing presence on every step.
 *
 * Remote characters are kept in a mutable store that the canvas reads every
 * frame. Each remote walks towards its last received target at the pace
 * the sender reported, so it moves like a person rather than easing in and
 * out between packets. React only re-renders on structural changes (join /
 * leave / status / look) via `useSyncExternalStore`.
 *
 * The two transports are deliberately tolerant of each other failing:
 * a `move` from someone presence hasn't announced yet creates a provisional
 * player straight away (presence sync regularly lags the first broadcasts,
 * and a dropped `track` must not leave someone walking around as a faded
 * offline silhouette), and the page polls the persisted positions while the
 * channel is down so the map keeps moving even without Realtime.
 */

export type PresenceStatus = 'online' | 'away';

export interface PresenceMeta {
  user_id: string;
  name: string;
  avatar_url: string | null;
  config: AvatarConfig;
  x: number;
  y: number;
  facing: Facing;
  status: PresenceStatus;
  at: number;
}

interface MovePayload {
  user_id: string;
  x: number;
  y: number;
  facing: Facing;
  moving: boolean;
  /** Sender's walking speed in world units / s (0 when they stopped). */
  s?: number;
}

interface EmotePayload {
  user_id: string;
  text: string;
}

export interface LivePlayer {
  userId: string;
  name: string;
  avatarUrl: string | null;
  config: AvatarConfig;
  status: PresenceStatus;
  /** Interpolated render position. */
  x: number;
  y: number;
  /** Last network target. */
  tx: number;
  ty: number;
  facing: Facing;
  moving: boolean;
  /** Pace to walk towards the target at (world units / s). */
  speed: number;
  /** Distance walked — drives the leg animation phase. */
  walked: number;
  /** `performance.now()` of the last `move` broadcast (0 = none yet). */
  lastUpdate: number;
  /**
   * Created from a `move` broadcast before presence announced them. Kept
   * until presence confirms or the broadcasts dry up.
   */
  provisional: boolean;
  emote: { text: string; until: number } | null;
}

export interface LastKnown {
  x: number;
  y: number;
  facing: Facing;
  at: number;
}

/** Look + name for someone we only know from a broadcast so far. */
export interface KnownIdentity {
  name: string;
  config: AvatarConfig;
}

/** Default pace for a remote whose sender didn't report one (old client). */
const DEFAULT_REMOTE_SPEED = 115;
/** Fastest a remote may be driven while catching up after a dropped packet. */
const MAX_CATCHUP_SPEED = 420;
/** Jump instead of walking when the target is this far away (teleport). */
const SNAP_DISTANCE = 160;
/** A provisional player with no broadcast for this long is treated as gone. */
const PROVISIONAL_TTL_MS = 15_000;
/** How long a `move` keeps someone looking "active" for status purposes. */
const RECENT_MOVE_MS = 2_000;

export class OfficeLiveStore {
  /** Remote players currently present (never includes the local user). */
  readonly players = new Map<string, LivePlayer>();
  /** Where people were standing when they went offline this session. */
  readonly lastKnown = new Map<string, LastKnown>();
  /** Local player's render position — written by the canvas every frame. */
  me: { x: number; y: number; facing: Facing } | null = null;
  /** Local emote (so our own bubble renders too). */
  myEmote: { text: string; until: number } | null = null;
  connected = false;
  /** Why the channel is down, when the server told us. Null while healthy. */
  error: string | null = null;
  /**
   * Resolves the look + name for a user id from whatever the page already
   * knows (persisted avatars, profiles). Used for provisional players.
   */
  resolveIdentity: ((userId: string) => KnownIdentity | null) | null = null;

  private selfId: string | null = null;
  private version = 0;
  private listeners = new Set<() => void>();

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  getVersion = () => this.version;

  /** Bump for structural changes that React should see. */
  emit() {
    this.version += 1;
    for (const fn of this.listeners) fn();
  }

  setSelf(userId: string | null) {
    this.selfId = userId;
  }

  /** True when a `move` from this player arrived in the last couple of seconds. */
  isActive(p: LivePlayer, now = performance.now()): boolean {
    return p.lastUpdate > 0 && now - p.lastUpdate < RECENT_MOVE_MS;
  }

  /**
   * Status to draw. Someone walking around is plainly not away, whatever a
   * stale presence meta (or a second, hidden tab) claims.
   */
  displayStatus(p: LivePlayer, now = performance.now()): PresenceStatus {
    return p.status === 'away' && this.isActive(p, now) ? 'online' : p.status;
  }

  private removePlayer(id: string, p: LivePlayer) {
    this.lastKnown.set(id, { x: p.tx, y: p.ty, facing: p.facing, at: Date.now() });
    this.players.delete(id);
  }

  /** Apply a full presence snapshot. */
  syncPresence(state: Record<string, PresenceMeta[]>, selfId: string) {
    this.selfId = selfId;
    const now = performance.now();
    const seen = new Set<string>();
    let changed = false;
    for (const [key, metas] of Object.entries(state)) {
      if (key === selfId) continue;
      // Multiple tabs → multiple metas; the freshest one wins.
      const meta = metas.reduce<PresenceMeta | null>(
        (best, m) => (!best || (m.at ?? 0) > (best.at ?? 0) ? m : best),
        null,
      );
      if (!meta || !meta.user_id || meta.user_id === selfId) continue;
      seen.add(meta.user_id);
      const existing = this.players.get(meta.user_id);
      const pos = sanitizePosition(meta.x, meta.y);
      if (existing) {
        const cfg = normalizeAvatar(meta.config);
        const status = meta.status ?? 'online';
        if (
          existing.provisional ||
          existing.name !== meta.name ||
          existing.status !== status ||
          existing.avatarUrl !== meta.avatar_url ||
          JSON.stringify(existing.config) !== JSON.stringify(cfg)
        ) {
          existing.name = meta.name;
          existing.status = status;
          existing.avatarUrl = meta.avatar_url ?? null;
          existing.config = cfg;
          existing.provisional = false;
          changed = true;
        }
        // Presence positions are coarse; only use them if we haven't heard
        // a move broadcast recently (e.g. we just joined).
        if (now - existing.lastUpdate > 1500) {
          existing.tx = pos.x;
          existing.ty = pos.y;
          existing.facing = meta.facing ?? 'down';
          existing.speed = DEFAULT_REMOTE_SPEED;
        }
      } else {
        this.players.set(meta.user_id, {
          userId: meta.user_id,
          name: meta.name,
          avatarUrl: meta.avatar_url ?? null,
          config: normalizeAvatar(meta.config),
          status: meta.status ?? 'online',
          x: pos.x,
          y: pos.y,
          tx: pos.x,
          ty: pos.y,
          facing: meta.facing ?? 'down',
          moving: false,
          speed: DEFAULT_REMOTE_SPEED,
          walked: 0,
          lastUpdate: 0,
          provisional: false,
          emote: null,
        });
        this.lastKnown.delete(meta.user_id);
        changed = true;
      }
    }
    for (const [id, p] of this.players) {
      if (seen.has(id)) continue;
      // Someone we only know from broadcasts stays until those stop too;
      // presence sync is allowed to lag the first steps.
      if (p.provisional && now - p.lastUpdate < PROVISIONAL_TTL_MS) continue;
      this.removePlayer(id, p);
      changed = true;
    }
    if (changed) this.emit();
  }

  applyMove(p: MovePayload) {
    if (!p || typeof p.user_id !== 'string' || p.user_id === this.selfId) return;
    const now = performance.now();
    const pos = sanitizePosition(p.x, p.y);
    let player = this.players.get(p.user_id);
    if (!player) {
      const known = this.resolveIdentity?.(p.user_id) ?? null;
      player = {
        userId: p.user_id,
        name: known?.name ?? 'Teammate',
        avatarUrl: null,
        config: known?.config ?? seededAvatar(p.user_id),
        status: 'online',
        x: pos.x,
        y: pos.y,
        tx: pos.x,
        ty: pos.y,
        facing: p.facing ?? 'down',
        moving: !!p.moving,
        speed: DEFAULT_REMOTE_SPEED,
        walked: 0,
        lastUpdate: now,
        provisional: true,
        emote: null,
      };
      this.players.set(p.user_id, player);
      this.lastKnown.delete(p.user_id);
      this.emit();
      return;
    }
    const reported = typeof p.s === 'number' && Number.isFinite(p.s) && p.s > 0 ? p.s : null;
    if (reported !== null) {
      player.speed = reported;
    } else if (player.lastUpdate > 0) {
      // Older client without a pace field: infer it from the last two targets.
      const elapsed = (now - player.lastUpdate) / 1000;
      if (elapsed > 0.03 && elapsed < 1) {
        const apparent = Math.hypot(pos.x - player.tx, pos.y - player.ty) / elapsed;
        if (apparent > 1) player.speed = Math.min(MAX_CATCHUP_SPEED, apparent);
      }
    }
    player.tx = pos.x;
    player.ty = pos.y;
    player.facing = p.facing ?? player.facing;
    player.moving = !!p.moving;
    player.lastUpdate = now;
  }

  applyEmote(p: EmotePayload) {
    const player = this.players.get(p.user_id);
    if (!player) return;
    player.emote = { text: p.text, until: performance.now() + 2600 };
    this.emit();
  }

  /** Per-frame interpolation of remote characters. */
  tick(dt: number) {
    const now = performance.now();
    let changed = false;
    for (const [id, p] of this.players) {
      if (p.provisional && now - p.lastUpdate > PROVISIONAL_TTL_MS) {
        this.removePlayer(id, p);
        changed = true;
        continue;
      }
      const dx = p.tx - p.x;
      const dy = p.ty - p.y;
      const dist = Math.hypot(dx, dy);
      if (dist > SNAP_DISTANCE) {
        p.x = p.tx;
        p.y = p.ty;
      } else if (dist > 0.01) {
        // Walk at the sender's pace, but never fall more than ~a packet and
        // a half behind: a late or dropped update is made up smoothly
        // instead of leaving the character trailing.
        const pace = Math.min(MAX_CATCHUP_SPEED, Math.max(p.speed, dist / 0.15));
        const step = Math.min(dist, pace * dt);
        p.x += (dx / dist) * step;
        p.y += (dy / dist) * step;
        p.walked += step;
      }
      if (p.emote && p.emote.until < now) {
        p.emote = null;
        changed = true;
      }
    }
    if (this.myEmote && this.myEmote.until < now) {
      this.myEmote = null;
      changed = true;
    }
    if (changed) this.emit();
  }

  setConnected(v: boolean, error?: string) {
    const nextError = v ? null : (error ?? this.error);
    if (this.connected !== v || this.error !== nextError) {
      this.connected = v;
      this.error = nextError;
      this.emit();
    }
  }

  clear() {
    this.players.clear();
    this.emit();
  }
}

/** Subscribe a component to structural store changes. */
export function useLiveStoreVersion(store: OfficeLiveStore): number {
  return useSyncExternalStore(store.subscribe, store.getVersion, store.getVersion);
}

export interface UseOfficeRealtimeArgs {
  organizationId: string | null | undefined;
  userId: string | null | undefined;
  name: string;
  avatarUrl: string | null;
  config: AvatarConfig | null;
  /** Position to announce on join (and to re-announce on re-track). */
  initialPosition: { x: number; y: number; facing: Facing };
  enabled: boolean;
}

export interface OfficeRealtime {
  store: OfficeLiveStore;
  status: PresenceStatus;
  /** Call from the game loop; throttled internally. */
  sendMove: (x: number, y: number, facing: Facing, moving: boolean, speed?: number) => void;
  sendEmote: (text: string) => void;
  setStatus: (status: PresenceStatus) => void;
  /** Office game channel (ping pong). Fire-and-forget, same topic as movement. */
  sendPong: (payload: unknown) => void;
  subscribePong: (fn: (payload: unknown) => void) => () => void;
}

const MOVE_INTERVAL_MS = 90;
/** Slow presence re-track so our announced spot never goes stale. */
const PRESENCE_HEARTBEAT_MS = 25_000;

function describeChannelError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string' && err) return err;
  return 'Lost connection to the live channel';
}

export function useOfficeRealtime(args: UseOfficeRealtimeArgs): OfficeRealtime {
  const store = useMemo(() => new OfficeLiveStore(), []);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const [status, setStatusState] = useState<PresenceStatus>('online');
  const [manualAway, setManualAway] = useState(false);

  // Latest local position — included in every re-track so a teammate who
  // joins later sees us in the right place straight away.
  const posRef = useRef(args.initialPosition);
  const lastSentRef = useRef(0);
  const pendingRef = useRef<MovePayload | null>(null);
  const timerRef = useRef<number | null>(null);
  const metaRef = useRef<Omit<PresenceMeta, 'x' | 'y' | 'facing' | 'at'> | null>(null);
  const pongListenersRef = useRef(new Set<(payload: unknown) => void>());

  const { organizationId, userId, name, avatarUrl, config, enabled } = args;
  const configKey = config ? JSON.stringify(config) : null;

  useEffect(() => {
    store.setSelf(userId ?? null);
  }, [store, userId]);

  const trackPresence = (pos: { x: number; y: number; facing: Facing }) => {
    const ch = channelRef.current;
    if (!ch || !store.connected || !metaRef.current) return;
    void ch
      .track({
        ...metaRef.current,
        x: pos.x,
        y: pos.y,
        facing: pos.facing,
        at: Date.now(),
      } satisfies PresenceMeta)
      .then((result) => {
        if (result !== 'ok') console.warn('[digital-office] presence track failed:', result);
      });
  };

  // Keep the identity half of the presence payload current.
  useEffect(() => {
    if (!userId || !config) {
      metaRef.current = null;
      return;
    }
    metaRef.current = {
      user_id: userId,
      name,
      avatar_url: avatarUrl,
      config,
      status,
    };
    // Re-track so teammates pick up the new look / status.
    trackPresence(posRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, name, avatarUrl, configKey, status, store]);

  // Auto-away when the tab is hidden; manual away sticks until cleared.
  useEffect(() => {
    const update = () => {
      setStatusState(manualAway || document.visibilityState === 'hidden' ? 'away' : 'online');
    };
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, [manualAway]);

  // Channel lifecycle.
  useEffect(() => {
    if (!enabled || !organizationId || !userId || !config) return;

    const topic = `digital-office:${organizationId}`;
    let cancelled = false;
    let channel: RealtimeChannel | null = null;
    let heartbeat: number | null = null;

    const setup = async () => {
      // `supabase.channel(topic)` hands back an existing channel for the same
      // topic, and one that is still waiting on its leave ack (previous
      // visit, StrictMode re-run) silently refuses to subscribe again. Make
      // sure any leftover is fully gone before we create ours.
      const stale = supabase.getChannels().find((c) => c.topic === `realtime:${topic}`);
      if (stale) await supabase.removeChannel(stale);
      if (cancelled) return;

      channel = supabase.channel(topic, {
        config: {
          presence: { key: userId },
          broadcast: { self: false, ack: false },
        },
      });
      channelRef.current = channel;
      const ch = channel;

      ch.on('presence', { event: 'sync' }, () => {
        store.syncPresence(ch.presenceState<PresenceMeta>(), userId);
      })
        .on('broadcast', { event: 'move' }, ({ payload }) => {
          store.applyMove(payload as MovePayload);
        })
        .on('broadcast', { event: 'emote' }, ({ payload }) => {
          store.applyEmote(payload as EmotePayload);
        })
        .on('broadcast', { event: 'pong' }, ({ payload }) => {
          for (const fn of pongListenersRef.current) fn(payload);
        })
        .subscribe((state, err) => {
          if (cancelled) return;
          if (state === 'SUBSCRIBED') {
            store.setConnected(true);
            trackPresence(posRef.current);
          } else if (state === 'CHANNEL_ERROR') {
            const reason = describeChannelError(err);
            console.warn('[digital-office] realtime channel error:', reason, err);
            store.setConnected(false, reason);
          } else if (state === 'TIMED_OUT') {
            console.warn('[digital-office] realtime channel timed out');
            store.setConnected(false, 'Connection timed out');
          } else if (state === 'CLOSED') {
            store.setConnected(false);
          }
        });

      heartbeat = window.setInterval(() => trackPresence(posRef.current), PRESENCE_HEARTBEAT_MS);
    };
    void setup();

    return () => {
      cancelled = true;
      store.setConnected(false);
      store.clear();
      if (heartbeat !== null) window.clearInterval(heartbeat);
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      channelRef.current = null;
      if (channel) void supabase.removeChannel(channel);
    };
    // Config changes re-track via the effect above; they must not tear the
    // channel down, hence `configKey` is deliberately not a dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, organizationId, userId, !!config, store]);

  const flushMove = () => {
    timerRef.current = null;
    const p = pendingRef.current;
    const ch = channelRef.current;
    if (!p || !ch || !store.connected) return;
    pendingRef.current = null;
    lastSentRef.current = performance.now();
    void ch.send({ type: 'broadcast', event: 'move', payload: p }).then((result) => {
      if (result !== 'ok') console.warn('[digital-office] move broadcast failed:', result);
    });
    // When a walk ends, refresh presence so late joiners get the resting
    // position without waiting for the next broadcast.
    if (!p.moving) trackPresence(p);
  };

  const sendMove: OfficeRealtime['sendMove'] = (x, y, facing, moving, speed) => {
    if (!userId) return;
    posRef.current = { x, y, facing };
    pendingRef.current = {
      user_id: userId,
      x,
      y,
      facing,
      moving,
      s: moving && speed && Number.isFinite(speed) ? Math.round(speed) : 0,
    };
    const since = performance.now() - lastSentRef.current;
    if (timerRef.current !== null) return;
    if (since >= MOVE_INTERVAL_MS) {
      flushMove();
    } else {
      timerRef.current = window.setTimeout(flushMove, MOVE_INTERVAL_MS - since);
    }
  };

  const sendEmote: OfficeRealtime['sendEmote'] = (text) => {
    if (!userId) return;
    store.myEmote = { text, until: performance.now() + 2600 };
    store.emit();
    const ch = channelRef.current;
    if (!ch || !store.connected) return;
    void ch.send({
      type: 'broadcast',
      event: 'emote',
      payload: { user_id: userId, text } satisfies EmotePayload,
    });
  };

  const setStatus = (next: PresenceStatus) => {
    setManualAway(next === 'away');
  };

  const sendPong = useCallback(
    (payload: unknown) => {
      const ch = channelRef.current;
      if (!ch || !store.connected) return;
      void ch.send({ type: 'broadcast', event: 'pong', payload });
    },
    [store],
  );

  const subscribePong = useCallback((fn: (payload: unknown) => void) => {
    pongListenersRef.current.add(fn);
    return () => {
      pongListenersRef.current.delete(fn);
    };
  }, []);

  return { store, status, sendMove, sendEmote, setStatus, sendPong, subscribePong };
}
